import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import userManualMarkdown from '../../../content/fleetUserManual.md';
import { 
  HiOutlineBookOpen, 
  HiOutlineLightBulb, 
} from 'react-icons/hi';

const resolveMarkdownAssetUrl = (src) => {
  if (!src) return src;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('//')) return src;
  if (src.startsWith('data:') || src.startsWith('blob:')) return src;
  if (src.startsWith('/')) return `${process.env.PUBLIC_URL || ''}${src}`;
  return src;
};

const UserManual = () => {
  const markdownContent = userManualMarkdown;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="px-6 py-8 sm:px-10 sm:py-10">
      <div className="prose prose-lg prose-slate max-w-none">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
                // Custom styling for markdown elements
                h1: ({children}) => (
                  <h1 className="mb-6 flex items-center gap-3 text-3xl font-bold text-slate-900 sm:text-4xl">
                    <div className="rounded-xl bg-blue-100 p-2.5">
                      <HiOutlineBookOpen size={24} className="text-blue-600" />
                    </div>
                    {children}
                  </h1>
                ),
                h2: ({children}) => (
                  <h2 className="mt-10 mb-5 border-b border-slate-200 pb-3 text-2xl font-semibold text-slate-900">
                    {children}
                  </h2>
                ),
                h3: ({children}) => (
                  <h3 className="mt-7 mb-3 text-xl font-semibold text-slate-900">
                    {children}
                  </h3>
                ),
                h4: ({children}) => (
                  <h4 className="mt-5 mb-2 text-lg font-medium text-slate-800">
                    {children}
                  </h4>
                ),
                p: ({children}) => (
                  <p className="mb-4 leading-8 text-slate-600">
                    {children}
                  </p>
                ),
                ul: ({children}) => (
                  <ul className="mb-5 list-disc space-y-2 pl-6 text-slate-600">
                    {children}
                  </ul>
                ),
                li: ({children}) => (
                  <li className="text-slate-600">
                    {children}
                  </li>
                ),
                blockquote: ({children}) => (
                  <blockquote className="my-6 rounded-2xl border border-blue-100 bg-blue-50/80 p-5 shadow-sm">
                    <div className="flex items-start gap-3">
                      <HiOutlineLightBulb size={20} className="mt-0.5 flex-shrink-0 text-blue-600" />
                      <div>
                        <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-blue-800">Quick Tips</h3>
                        <div className="text-sm text-blue-700">
                          {children}
                        </div>
                      </div>
                    </div>
                  </blockquote>
                ),
                img: ({src, alt}) => (
                  <figure className="my-8 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm">
                    <img 
                      src={resolveMarkdownAssetUrl(src)} 
                      alt={alt || 'Image'} 
                      className="h-auto w-full"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden border-t border-dashed border-slate-300 bg-slate-100 p-6">
                      <div className="text-center">
                        <div className="mb-2 text-sm text-slate-500">Image failed to load</div>
                        <div className="text-xs font-mono text-slate-400">{src}</div>
                        <div className="mt-2 text-xs text-slate-500">
                          This will show the actual interface screenshot for {alt || 'this section'}
                        </div>
                      </div>
                    </div>
                    {alt ? (
                      <figcaption className="border-t border-slate-200 bg-white px-5 py-3 text-sm text-slate-500">
                        {alt}
                      </figcaption>
                    ) : null}
                  </figure>
                ),
                a: ({href, children}) => (
                  <a 
                    href={href} 
                    className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800"
                    target={href?.startsWith('http') ? '_blank' : undefined}
                    rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {children}
                  </a>
                ),
                hr: () => (
                  <hr className="my-10 border-slate-200" />
                ),
                em: ({children}) => (
                  <em className="my-3 block text-center text-sm italic text-slate-500">
                    {children}
                  </em>
                )
          }}
        >
          {markdownContent}
        </ReactMarkdown>
      </div>
      </div>
      </div>
    </div>
  );
};

export default UserManual;
